import { type ReactiveController, type ReactiveElement } from "lit";

import type { IRpcResponse } from "api-contracts/rpc";
import type { AuthParams, AuthResult, AuthRole, AuthUser } from "api-contracts/rpc/auth";

import { queryClient } from "./data-query-controller";

let __autoClearSession = true;
export type Role = AuthRole;
export type ICurrentUser = AuthUser;

class AppContext {
  private _currentUser?: ICurrentUser;

  set onSingInCallback(cb: (user: ICurrentUser) => void) {
    this._onSingInCallback = cb;
    if (this.currentUser) {
      this._onSingInCallback(this.currentUser);
    }
  }
  get onSingInCallback() {
    return this._onSingInCallback;
  }

  private _onSingInCallback: (user: ICurrentUser) => void = () => {};

  onSignOutCallback: () => void = () => {};

  get currentUser(): ICurrentUser | undefined {
    return this._currentUser;
  }

  set currentUser(value: ICurrentUser | undefined) {
    if (this.currentUser === value) return;

    this._currentUser = value;
    AppContext.controllers.forEach((controller) => controller.notifyUserChange());
  }

  private static controllers: AppContextController[] = [];

  static registerController(controller: AppContextController): void {
    this.controllers.push(controller);
  }

  static unregisterController(controller: AppContextController): void {
    const index = this.controllers.indexOf(controller);
    if (index !== -1) {
      this.controllers.splice(index, 1);
    }
  }

  private accessToken?: string;

  private _updateAccessToken(token: string): void {
    this.accessToken = token;
  }

  async processRpcResponse<T>(response: Response): Promise<T> {
    const refreshedToken = response.headers.get("x-access-token-refreshed");
    if (refreshedToken) {
      this._updateAccessToken(refreshedToken);
    }

    if (!response.ok) {
      if (response.status === 401 && __autoClearSession) {
        this.signOut();
      }
      throw new Error("Request failed");
    }

    const data = await response.json();
    return data as T;
  }

  private async processResponse<T>(response: Response): Promise<T> {
    const data = await this.processRpcResponse<T>(response);
    return data;
  }

  async SignIn(username: string, password: string): Promise<ICurrentUser | undefined> {
    try {
      const response = await fetch("/api/rpc/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password } as AuthParams),
      });

      const data = await this.processResponse<IRpcResponse<AuthResult>>(response);

      if (!data.success || !data.data) {
        return undefined;
      }

      const { accessToken } = data.data;
      this.signIn(accessToken);
    } catch (error) {
      this.signOut();
      throw error; // Re-throw to let caller handle the error
    }
    return this.currentUser;
  }

  public get isSuperuser(): boolean {
    return this.currentUser?.roles.some((role) => role === "superuser") ?? false;
  }

  constructor() {
    // Attempt to sign in using session cookie
    this.ready = this.signInWithSession();
  }

  public ready;

  async signInWithSession(): Promise<void> {
    try {
      const response = await fetch("/api/rpc/auth-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await this.processResponse<IRpcResponse<AuthResult>>(response);

      if (!data.success || !data.data) {
        return;
      }

      const { accessToken } = data.data;
      this.signIn(accessToken);
    } catch {
      // Do nothing, user is not signed in
    }
  }

  get isAuthenticated(): boolean {
    return this.currentUser !== undefined;
  }

  get currentAccessToken(): string | undefined {
    return this.accessToken;
  }

  private _currentPublicProfile?: string | undefined;
  public get currentPublicProfile(): string | undefined {
    return this._currentPublicProfile;
  }
  public set currentPublicProfile(value: string | undefined) {
    this._currentPublicProfile = value;
    AppContext.controllers.forEach((controller) => controller.notifyUserChange());
  }

  public signIn(accessToken: string): void {
    try {
      const payload = jwtDecode(accessToken) as {
        sub: string;
        name: string;
        roles: Role[];
        exp: number;
        avatarUrl?: string;
        handle?: string;
        memberSinceDate?: string;
        preferences?: { themes: string[] };
        creatorsFollowing?: string[];
      } | null;

      if (!payload) {
        this.signOut();
        return;
      }

      this.accessToken = accessToken;

      this.currentUser = {
        id: payload.sub,
        name: payload.name,
        roles: payload.roles,
        avatarUrl: payload.avatarUrl,
        handle: payload.handle,
        memberSinceDate: payload.memberSinceDate,
        preferences: payload.preferences,
        creatorsFollowing: payload.creatorsFollowing,
      };
      this.onSingInCallback(this.currentUser);
    } catch (e) {
      console.log(e);
      this.signOut();
    }
  }

  async signOut(): Promise<void> {
    if (!this.isAuthenticated) {
      return;
    }
    try {
      await fetch("/api/rpc/sign-out", {
        method: "POST",
      });
    } finally {
      this.currentUser = undefined;
      this.accessToken = undefined;
      this.onSignOutCallback();

      queryClient.clear();

      AppContext.controllers.forEach((controller) => controller.notifyUserChange());
    }
  }

  dispose(): void {
    // No-op
  }
}

export function jwtDecode(token: string) {
  try {
    const parts = token.split(".") as [string, string, string];
    if (parts.length !== 3) throw new Error("Invalid JWT format");

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Invalid JWT", e);
    return null;
  }
}

export function initAppContext(options: { autoClearSession: boolean }) {
  __autoClearSession = options.autoClearSession;
}

export const appContext = new AppContext();

export class AppContextController implements ReactiveController {
  constructor(private host: ReactiveElement) {
    host.addController(this);
    AppContext.registerController(this);
  }

  beforeUserChanged = (): void => {};

  appContext = appContext;

  hostConnected(): void {
    this.beforeUserChanged();
  }

  hostDisconnected(): void {
    AppContext.unregisterController(this);
  }

  notifyUserChange(): void {
    this.beforeUserChanged();
    this.host.requestUpdate();
  }
}
